import {
  faClose,
  faInfoCircle,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import {
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { TemplateWrapper } from './Wrapper';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { useRef } from 'react';
import { faMicrophone } from '@fortawesome/pro-solid-svg-icons';
import _ from 'lodash';
import { InputNumber } from '@design-system/components/input/inputNumber';
import {
  MeasurementMode,
  Media,
  RunningExerciseSet,
  RunningExerciseType,
  RunningOutdoor,
} from '../../models/workout.domain';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  EditWorkoutVM,
  MenuItems,
} from '../../view-models/workoutEdit.viewModel';
import { Running as RunningType } from '../../models/workout.domain';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { IconButton } from '@design-system/components/button/iconButton';
import { clsx } from 'clsx';
import { ArrayItem, toOptionsConstructor } from '@client/new/helpers/types';
import { RadioSelect } from '@client/new/pages/promotions/view/components/RadioSelect';
import { space } from '@design-system/tokens';
import { Row } from '@design-system/components/grid/grid';
import { TimeInput } from '@design-system/components/time-input';

type RunningVariant =
  | RunningType<RunningExerciseType.Treadmill>
  | RunningType<RunningExerciseType.Outdoor>;

type RunningProps = {
  index: number;
  header: {
    icon: IconProp;
    title: string;
  };
  menuItems: MenuItems;
  expand: boolean;
} & RunningVariant &
  Pick<
    EditWorkoutVM,
    'influencerId' | 'dispatch' | 'control' | 'submitting' | 'readonly'
  >;

const RunningTooltipText = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <Typography variant="body" size="small">
      Program Running workouts and add audio cues if you want! Learn about our
      template{' '}
      <a
        href="https://help.playbookapp.io/en/articles/11473567-how-to-use-the-running-template"
        target="_blank"
        rel="noreferrer"
        style={{
          textDecoration: 'underline',
        }}
      >
        here
      </a>
    </Typography>
  </div>
);

const formatDurationToHHMMSS = (totalSeconds: number): string => {
  totalSeconds = Math.floor(totalSeconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};
const formatDistanceToMiles = (distance: number): string => {
  return `${distance.toFixed(2).padStart(5, '0')} mi`;
};

export const Running = (p: RunningProps) => {
  const total = p.sets.reduce(
    (acc, s) => ({
      distance: acc.distance + ((s as ArrayItem<OutdoorSets>)?.distance || 0),
      duration: acc.duration + (s.time || 0),
    }),
    { duration: 0, distance: 0 },
  );
  const mode =
    p.variant === RunningExerciseType.Treadmill
      ? 'time-based'
      : p.measurementMode || 'time-based';
  const totalText =
    mode === 'time-based' ? 'Total duration:' : 'Total distance:';
  const v = mode === 'time-based' ? total.duration : total.distance;
  const totalValue =
    mode === 'time-based'
      ? formatDurationToHHMMSS(v)
      : formatDistanceToMiles(v);

  return (
    <div>
      <TemplateWrapper
        name={p.name}
        menuItems={p.menuItems}
        header={p.header}
        expand={p.expand}
      >
        <div className="bg-white w-full flex p-4 gap-6 rounded-b-xl flex-col">
          <Row alignCenter gap="l">
            <ToggleButtonGroup
              value={p.variant}
              exclusive
              onChange={(ev, value) => {
                p.dispatch('changeRunningVariant', {
                  index: p.index,
                  variant: value,
                });
              }}
              data-testid="runningTypeToggle"
              disabled={p.readonly}
            >
              <ToggleButton
                value={RunningExerciseType.Outdoor}
                sx={{
                  borderColor: 'black',
                  color: 'black',
                  height: '40px',
                  borderRadius: '10px',
                  fontWeight: 500,
                  fontFamily: 'Visuelt',
                  '&.Mui-selected': {
                    background: 'black',
                    color: 'white',
                    fontWeight: 500,
                  },
                  '&.Mui-selected:hover': {
                    background: 'black',
                    color: 'white',
                    fontWeight: 500,
                  },
                }}
              >
                Outdoor
              </ToggleButton>
              <ToggleButton
                value={RunningExerciseType.Treadmill}
                sx={{
                  color: 'black',
                  height: '40px',
                  borderColor: 'black',
                  borderRadius: '10px',
                  fontWeight: 500,
                  fontFamily: 'Visuelt',
                  '&.Mui-selected': {
                    background: 'black',
                    color: 'white',
                    fontWeight: 500,
                  },
                  '&.Mui-selected:hover': {
                    background: 'black',
                    color: 'white',
                    fontWeight: 500,
                  },
                }}
              >
                Treadmill
              </ToggleButton>
            </ToggleButtonGroup>
            <div>
              <Tooltip
                title={<RunningTooltipText />}
                placement="right-start"
                componentsProps={{
                  tooltip: {
                    open: true,
                    sx: {
                      maxWidth: '170px',
                      bgcolor: 'black',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      borderRadius: '10px',
                      width: 'max-content',
                      p: '4px 8px',
                    },
                  },
                }}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </div>
          </Row>
          {p.variant === 'Outdoor' ? (
            <RadioSelect
              reversed
              options={measurementTypes}
              name="measurementType"
              value={p.measurementMode || 'time-based'}
              onChange={(e) =>
                p.dispatch('changeMeasurementMode', {
                  measurementMode: e.target.value as MeasurementMode,
                  index: p.index,
                })
              }
            />
          ) : null}
          <Typography className="w-[205px] h-[40px] flex items-center">
            <b>{totalText}</b>
            <span style={{ paddingLeft: '16px' }}>{totalValue}</span>
          </Typography>
          {p.variant === 'Outdoor' ? (
            <RunningSetsOutdoor
              sets={p.sets as OutdoorSets}
              measurementMode={p.measurementMode || 'time-based'}
              exerciseIndex={p.index}
              control={p.control}
              dispatch={p.dispatch}
              submitting={p.submitting}
              readonly={p.readonly}
            />
          ) : (
            <RunningSetsTreadmill
              sets={p.sets as TreadmillSets}
              exerciseIndex={p.index}
              control={p.control}
              dispatch={p.dispatch}
              submitting={p.submitting}
              readonly={p.readonly}
            />
          )}
          <Audio
            title="Run complete"
            audio={p.media}
            loading={!!p.uploading}
            addAudio={(file: File) =>
              p.dispatch('addRunCompleteAudio', {
                index: p.index,
                uuid: p.uuid || '',
                file,
              })
            }
            removeAudio={() => p.dispatch('removeRunCompleteAudio', p.index)}
            disabled={p.readonly}
          />
        </div>
      </TemplateWrapper>
    </div>
  );
};

type TreadmillSets = RunningExerciseSet[RunningExerciseType.Treadmill][];
type RunningSetsTreadmillProps = {
  exerciseIndex: number;
  sets: TreadmillSets;
} & Pick<EditWorkoutVM, 'dispatch' | 'control' | 'submitting' | 'readonly'>;

const RunningSetsTreadmill = ({
  sets,
  exerciseIndex,
  control,
  dispatch,
  submitting,
  readonly,
}: RunningSetsTreadmillProps) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2 items-center">
        <div
          className="grid items-start md:items-end gap-2"
          style={{
            gridTemplateColumns: '110px 110px 110px',
          }}
        >
          <Label text="Duration" />
          <Label text="Speed" />
          <Label text="Incline" />
        </div>
      </div>
      {sets.map(({ uuid, uploading, audio }, i: number) => (
        <Controller
          key={uuid}
          name={`exercises.${exerciseIndex}.sets.${i}`}
          control={control}
          render={({ field: { onChange, value }, fieldState }) => (
            <div className="flex items-center">
              <div
                className="grid items-start md:items-end gap-2"
                style={{
                  gridTemplateColumns: '110px 110px 110px 1fr 50px',
                }}
              >
                <TimeInput
                  disabled={submitting || readonly}
                  onChange={(e: any) => {
                    onChange({ ...value, time: e.target.value });
                  }}
                  value={value.time}
                  min={1}
                  max={120}
                />
                <InputNumber
                  disabled={submitting || readonly}
                  value={value.speed}
                  onChange={(newValue) => {
                    onChange({ ...value, speed: newValue });
                  }}
                  error={fieldState.error?.message}
                  endCta={<Typography>mph</Typography>}
                  allowDecimal={true}
                  max={12}
                  min={0}
                />
                <InputNumber
                  disabled={submitting || readonly}
                  value={value.incline}
                  onChange={(newValue) =>
                    onChange({ ...value, incline: newValue })
                  }
                  error={fieldState.error?.message}
                  endCta={<Typography>%</Typography>}
                  allowDecimal={true}
                  max={14}
                  min={0}
                />
                <div>
                  <Audio
                    title="Add voice"
                    audio={audio}
                    loading={!!uploading}
                    addAudio={(file: File) =>
                      dispatch('addSetAudio', {
                        exerciseIndex: exerciseIndex,
                        setIndex: i,
                        uuid,
                        file,
                      })
                    }
                    removeAudio={() =>
                      dispatch('removeSetAudio', {
                        exerciseIndex: exerciseIndex,
                        setIndex: i,
                      })
                    }
                    disabled={readonly}
                  />
                </div>
                <IconButton
                  variant="text"
                  color="neutral"
                  icon={faClose}
                  onClick={() =>
                    dispatch('removeSet', { exercise: exerciseIndex, set: i })
                  }
                  disabled={readonly}
                />
              </div>
            </div>
          )}
        />
      ))}
      <Button
        color="primaryTonal"
        leftIcon={faPlus}
        className="w-[83px]"
        onClick={() => dispatch('addTreadmillSet', exerciseIndex)}
        disabled={readonly}
      >
        Set
      </Button>
    </div>
  );
};
const measurementType = toOptionsConstructor<MeasurementMode>();
export const measurementTypes = [
  measurementType('time-based', 'Time based'),
  measurementType('distance-based', 'Distance based'),
];
const Label = (p: { text: string }) => (
  <Typography
    variant="body"
    size="medium"
    fontWeight="medium"
    style={{ marginBottom: space.s }}
  >
    {p.text}
  </Typography>
);

type OutdoorSets = RunningExerciseSet[RunningExerciseType.Outdoor][];
type RunningSetsOutdoorProps = {
  exerciseIndex: number;
  sets: OutdoorSets;
} & Pick<EditWorkoutVM, 'dispatch' | 'control' | 'submitting' | 'readonly'> &
  Required<Pick<RunningOutdoor, 'measurementMode'>>;
const RunningSetsOutdoor = ({
  sets,
  exerciseIndex,

  control,
  dispatch,
  submitting,

  readonly,
  ...p
}: RunningSetsOutdoorProps) => {
  return (
    <div className="flex flex-col gap-3">
      <Label
        text={p.measurementMode === 'distance-based' ? 'Distance (mi)' : 'Time'}
      />
      {sets.map(({ uuid, uploading, audio }, i: number) => (
        <Controller
          key={uuid}
          name={`exercises.${exerciseIndex}.sets.${i}`}
          control={control}
          render={({ field: { onChange, value }, fieldState }) => (
            <div className="flex gap-0 items-center">
              <div className="flex items-start md:items-end gap-2 flex-col md:flex-row">
                {p.measurementMode === 'distance-based' ? (
                  <InputNumber
                    disabled={submitting || readonly}
                    value={value.distance}
                    min={0.05}
                    max={400}
                    onChange={(distance) =>
                      onChange({
                        ...value,
                        distance,
                      })
                    }
                    error={fieldState.error?.message}
                    endCta={<Typography>mil</Typography>}
                    wrapperClassName="w-[110px]"
                    allowDecimal={true}
                  />
                ) : (
                  <TimeInput
                    disabled={submitting || readonly}
                    onChange={(e: any) =>
                      onChange({ ...value, time: e.target.value })
                    }
                    value={value.time}
                    min={1}
                    max={14369}
                  />
                )}

                <div>
                  <Audio
                    title="Add voice"
                    audio={audio}
                    loading={!!uploading}
                    addAudio={(file: File) =>
                      dispatch('addSetAudio', {
                        exerciseIndex,
                        setIndex: i,
                        uuid,
                        file,
                      })
                    }
                    removeAudio={() =>
                      dispatch('removeSetAudio', {
                        exerciseIndex,
                        setIndex: i,
                      })
                    }
                    disabled={readonly}
                  />
                </div>
                {sets.length === 1 ? null : (
                  <IconButton
                    color="neutral"
                    variant="text"
                    icon={faClose}
                    onClick={() =>
                      dispatch('removeSet', { exercise: exerciseIndex, set: i })
                    }
                    disabled={readonly}
                  />
                )}
              </div>
            </div>
          )}
        />
      ))}
      <Button
        color="primaryTonal"
        leftIcon={faPlus}
        className="w-[83px]"
        onClick={() => dispatch('addOutdoorSet', exerciseIndex)}
        disabled={readonly}
      >
        Set
      </Button>
    </div>
  );
};

const Audio = ({
  title,
  addAudio,
  audio,
  removeAudio,
  loading,
  disabled,
}: {
  title: string;
  addAudio: (file: File) => void;
  audio?: Media;
  removeAudio: () => void;
  loading: boolean;
  disabled?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const addMedia = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.files && !_.isEmpty(ev.target.files)) {
      addAudio(ev.target.files[0]);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center w-[165px]">
        <CircularProgress className="w-[20px] h-[20px]" />
      </div>
    );
  }
  if (!audio) {
    return (
      <>
        <Button
          onClick={() => inputRef.current?.click()}
          leftIcon={faMicrophone}
          variant="text"
          className="w-[165px]"
          disabled={disabled}
        >
          {title}
        </Button>
        <input
          type="file"
          accept="audio/x-m4a"
          ref={inputRef}
          onChange={addMedia}
          hidden
        />
      </>
    );
  }
  return (
    <div className="flex items-center bg-[#F4F4F4] rounded-[20px] max-w-[330px] h-[40px] overflow-hidden">
      <audio
        controls
        src={`${audio.cdn01}${audio.audio}`}
        className="h-full max-h-[40px]"
        controlsList="nodownload noplaybackrate"
      />
      <FontAwesomeIcon
        icon={faClose}
        onClick={removeAudio}
        className={clsx(
          'pl-2 pr-2 cursor-pointer text-sm',
          disabled && 'pointer-events-none',
        )}
      />
    </div>
  );
};
